export type ErrorSource = 'vue' | 'promise' | 'window' | 'axios'

export interface NormalizedError {
  source: ErrorSource
  message: string
  reason: unknown
  stack: string | null
  url?: string
  line?: number
  axiosCode?: string
  axiosStatus?: number
  axiosOriginalMessage?: string
}

interface NoiseFilter {
  name: string
  appliesTo: readonly ErrorSource[]
  test: (err: NormalizedError) => boolean
}

// Safari's placeholder for the source URL of a script it refuses to attribute —
// see `safariMaskedCrossOrigin` below.
const SAFARI_MASKED_URL = 'webkit-masked-url://hidden/'

// The subset of `Error` we actually read, plus the non-standard positional
// properties Safari hangs off its error objects (`sourceURL` / `line` /
// `column`). New Relic's own `UncaughtError` cast copies those across when the
// thrown value fails its `instanceof Error` check, so they are frequently the
// only origin signal an error still carries by the time it reaches us.
export interface ErrorLike {
  name?: string
  message: string
  stack?: string
  sourceURL?: string
  filename?: string
}

/**
 * Duck-typed stand-in for `instanceof Error`.
 *
 * An error thrown by a script running in another realm — an injected
 * third-party tag, a browser extension's isolated world, an iframe — carries
 * that realm's `Error.prototype`, so `instanceof Error` is false for it in
 * ours. Testing the shape instead keeps the original `message`, `stack` and
 * Safari's `sourceURL` reachable, rather than discarding them and re-wrapping
 * the error at whichever handler caught it.
 */
export function isErrorLike(value: unknown): value is ErrorLike {
  return (
    typeof value === 'object' && value !== null && typeof (value as ErrorLike).message === 'string'
  )
}

/** `message` of an error-like value, falling back to its string form. */
export function getErrorMessage(value: unknown): string {
  return isErrorLike(value) ? value.message : String(value)
}

/** `stack` of an error-like value, or null when it carries none. */
export function getErrorStack(value: unknown): string | null {
  return isErrorLike(value) && typeof value.stack === 'string' ? value.stack : null
}

// Cross-origin script errors in Chrome arrive as the literal "Script error."
// with no url/line/error object — the browser strips the source for privacy
// when the script isn't served with CORS headers. iOS Safari's equivalent is
// `webkit-masked-url://hidden/`, handled by `safariMaskedCrossOrigin`.
const chromeCrossOriginMasked: NoiseFilter = {
  name: 'chromeCrossOriginMasked',
  appliesTo: ['window'],
  test: ({ message, url, line, reason }) =>
    !isErrorLike(reason) &&
    message === 'Script error.' &&
    !url &&
    (line === undefined || line === 0)
}

// Safari masks the source of cross-origin scripts whose responses lack CORS
// headers, reporting them as `webkit-masked-url://hidden/`. When the throw is
// attributed entirely to a masked source it originated outside our bundle —
// equivalent to Chrome's `Script error.` cross-origin masking above. Our own
// assets are served same-origin (no `base` in `vite.config.ts`), so a masked
// source is never us.
//
// Two shapes have to be recognised, because a masked error may or may not
// still carry a stack by the time it reaches a handler:
//   1. a stack whose every frame is masked, and
//   2. no usable stack, but Safari's `sourceURL` annotation pointing at the
//      masked URL — the shape New Relic's `UncaughtError` cast produces.
const safariMaskedCrossOrigin: NoiseFilter = {
  name: 'safariMaskedCrossOrigin',
  appliesTo: ['promise', 'window'],
  test: ({ reason, url }) => {
    if (url === SAFARI_MASKED_URL) return true
    if (!isErrorLike(reason)) return false

    const frames = (reason.stack ?? '')
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0 && line.includes('@'))

    if (frames.length > 0) return frames.every((frame) => frame.includes(SAFARI_MASKED_URL))

    return reason.sourceURL === SAFARI_MASKED_URL || reason.filename === SAFARI_MASKED_URL
  }
}

const EXTENSION_SCHEMES = [
  'chrome-extension', // Chrome, Edge and other Chromium browsers
  'moz-extension', // Firefox
  'safari-web-extension',
  'safari-extension' // legacy Safari
]
const STACK_URL = /\b[a-z][a-z0-9+.-]*:\/\/[^\s)]+/gi

function isExtensionUrl(url: string | undefined): boolean {
  return !!url && EXTENSION_SCHEMES.some((scheme) => url.startsWith(`${scheme}://`))
}

// Browser extensions that inject scripts into the page, such as Urban VPN, raise
// errors and rejections that our listeners catch. Drop them only when every URL
// in the stack is an extension's. An extension that wraps `fetch` or XHR still
// shows its frames over our own, and those errors stay reported.
const browserExtension: NoiseFilter = {
  name: 'browserExtension',
  appliesTo: ['vue', 'promise', 'window'],
  test: ({ reason, url }) => {
    if (isExtensionUrl(url)) return true
    if (!isErrorLike(reason)) return false

    // V8 frames start with `at `; Firefox and Safari frames contain `@`.
    const urls = (reason.stack ?? '')
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.startsWith('at ') || line.includes('@'))
      .flatMap((frame) => frame.match(STACK_URL) ?? [])

    if (urls.length > 0) return urls.every(isExtensionUrl)

    return isExtensionUrl(reason.sourceURL) || isExtensionUrl(reason.filename)
  }
}

// WebKit fires `TypeError: Load failed` (no stack) for native fetch/preload
// failures — backgrounded tabs on cellular, modulepreload misses, mid-flight
// navigation aborts. No JS frames means no actionable signal.
const safariNetwork: NoiseFilter = {
  name: 'safariNetwork',
  appliesTo: ['promise'],
  test: ({ message, reason }) => {
    if (message !== 'Load failed') return false
    return !isErrorLike(reason) || !reason.stack
  }
}

// Microsoft Outlook / Office 365 Safe Links scans pages it crawls and injects
// a script that throws this exact signature into the page context. The errors
// do not originate from our bundle.
const outlookSafeLinks: NoiseFilter = {
  name: 'outlookSafeLinks',
  appliesTo: ['vue', 'promise', 'window'],
  test: ({ message }) =>
    /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/.test(message)
}

// A third-party script (browser extension / injected vendor pixel) filters
// events by `type === 'ParityDigital'` and throws when its target collection
// is undefined. The 'ParityDigital' literal does not appear anywhere in our
// bundle.
const parityDigital: NoiseFilter = {
  name: 'parityDigital',
  appliesTo: ['vue', 'promise', 'window'],
  test: ({ message }) => message.includes("'ParityDigital'") || message.includes('"ParityDigital"')
}

// Manually-verified third-party rejection: "null is not an object (evaluating
// 'o.id')" fires across multiple CADY apps on different domains (booking and
// shop), so it cannot originate in either bundle. Retained as a backstop for
// the case where the rejection reaches us with neither a stack nor Safari's
// `sourceURL` annotation, leaving `safariMaskedCrossOrigin` nothing to match
// on and the message signature as the only identifier.
const thirdPartyOId: NoiseFilter = {
  name: 'thirdPartyOId',
  appliesTo: ['promise', 'window'],
  test: ({ message }) => message.includes("null is not an object (evaluating 'o.id')")
}

// Benign browser warning emitted when a ResizeObserver callback triggers
// further layout changes the browser cannot deliver in the same frame.
// Recovers on the next frame.
const resizeObserverLoop: NoiseFilter = {
  name: 'resizeObserverLoop',
  appliesTo: ['window'],
  test: ({ message }) => message.includes('ResizeObserver loop')
}

// Post-deploy stale-chunk errors: the user's open tab references a Vite
// bundle hash that no longer exists on the CDN. Lazy route/component imports
// and CSS modulepreloads fail with these signatures. The user recovers on
// refresh; nothing in our bundle is broken.
const staleChunk: NoiseFilter = {
  name: 'staleChunk',
  appliesTo: ['promise'],
  test: ({ message }) =>
    message.includes('Importing a module script failed') ||
    message.includes('Unable to preload CSS for') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module')
}

// Network errors are typically user-side (poor connectivity, iOS
// backgrounding) — not actionable engineering signal.
const axiosNetwork: NoiseFilter = {
  name: 'axiosNetwork',
  appliesTo: ['axios'],
  test: ({ axiosCode, axiosOriginalMessage }) =>
    axiosCode === 'ERR_NETWORK' || axiosOriginalMessage === 'Network Error'
}

// Axios rejects with `Request aborted` (code `ECONNABORTED`) when the XHR is
// aborted mid-flight — page navigation, user-cancelled requests, or external
// scanners abandoning the page. Real timeouts surface as `timeout of Xms
// exceeded` and are NOT filtered.
const axiosAbort: NoiseFilter = {
  name: 'axiosAbort',
  appliesTo: ['axios'],
  test: ({ axiosCode, axiosOriginalMessage }) =>
    axiosCode === 'ECONNABORTED' && axiosOriginalMessage === 'Request aborted'
}

const NOISE_FILTERS: readonly NoiseFilter[] = [
  chromeCrossOriginMasked,
  safariMaskedCrossOrigin,
  browserExtension,
  safariNetwork,
  outlookSafeLinks,
  parityDigital,
  thirdPartyOId,
  resizeObserverLoop,
  staleChunk,
  axiosNetwork,
  axiosAbort
]

export function isNoise(err: NormalizedError): boolean {
  return NOISE_FILTERS.some((filter) => filter.appliesTo.includes(err.source) && filter.test(err))
}
